import * as echarts from 'echarts/core'
import {
  DatasetComponent,
  DatasetComponentOption,
  DataZoomComponent,
  DataZoomComponentOption,
  GraphicComponent,
  GridComponent,
  GridComponentOption,
  LegendComponent,
  LegendComponentOption,
  MarkAreaComponent,
  MarkAreaComponentOption,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  TooltipComponentOption,
  VisualMapComponent,
} from 'echarts/components'
import {
  BarChart,
  BarSeriesOption,
  LineChart,
  LineSeriesOption,
  MapChart,
  PieChart,
  RadarChart,
  ScatterChart,
  ScatterSeriesOption
} from 'echarts/charts'
import { LabelLayout, UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import letterAspectRatio from './letterAspectRatio'
import { getViewport } from '/@/utils/domUtil'

echarts.use([
  DatasetComponent,
  DataZoomComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  ScatterChart,
  PieChart,
  RadarChart,
  MapChart,
  CanvasRenderer,
  UniversalTransition,
  LabelLayout,
  MarkAreaComponent,
  TitleComponent,
  GraphicComponent,
  ToolboxComponent,
  VisualMapComponent
])

export const echartsInstance = echarts

export type EChartsOption = echarts.ComposeOption<
  | DatasetComponentOption
  | DataZoomComponentOption
  | TooltipComponentOption
  | GridComponentOption
  | LegendComponentOption
  | BarSeriesOption
  | LineSeriesOption
  | ScatterSeriesOption
  | MarkAreaComponentOption
>;


export type EChartsRendererType = 'canvas' | 'svg';

type EChartsInitOpts = {
  locale?: string;
  renderer?: EChartsRendererType;
  devicePixelRatio?: number;
  useDirtyRect?: boolean;
  useCoarsePointer?: boolean;
  pointerSize?: number;
  ssr?: boolean;
  width?: number | string;
  height?: number | string;
};

export const initEcharts = (dom?: HTMLDivElement | HTMLCanvasElement, theme?: Object | string, opts?: EChartsInitOpts | Object) => {
  return echarts.init(dom, theme || null, opts || {})
}

export const connectEcharts = (charts: string | []) => {
  return echarts.connect(charts)
}

export const disconnectEcharts = (group: string) => {
  return echarts.disconnect(group)
}

/**
 *
 * @param letter the letter
 * @param fontSize
 * @return the letter's width
 */
export const getLetterWidth = (letter: string, fontSize: number) => {
  return fontSize * (letterAspectRatio[letter] || 1)
}

/**
 *
 * @param text the text
 * @param fontSize
 * @return the text's size
 */
export const getTextSize = (text: string, fontSize: number) => {
  let width = 0
  const pattern = new RegExp('[\u4E00-\u9FA5]+')
  text.split('').forEach(function (letter) {
    if (pattern.test(letter)) {
      // 中文字符
      width += fontSize
    } else {
      width += getLetterWidth(letter, fontSize)
    }
  })
  return width
}

/**
 * 计算当前文本是否需要换行
 * @param text
 * @param width
 * @param context
 * @returns {number}
 */
const findWrapBreak =  (text: string, width: number, context: CanvasRenderingContext2D) => {
  let min = 0
  let max: number = text.length - 1

  while (min <= max) {
    const middle = Math.floor((min + max) / 2)
    const middleWidth = context.measureText(text.substr(0, middle)).width
    const oneCharWiderThanMiddleWidth = context.measureText(text.substr(0, middle + 1)).width
    if (middleWidth <= width && oneCharWiderThanMiddleWidth > width) {
      return middle
    }
    if (middleWidth < width) {
      min = middle + 1
    } else {
      max = middle - 1
    }
  }

  return -1
}

const calculateWrapBreak = (
  text: string,
  width: number,
  context: CanvasRenderingContext2D,
  fontSize: number,
  useAspectRatio: boolean
): number => {
  let min = 0
  let max = text.length - 1

  while (min <= max) {
    const middle = Math.floor((min + max) / 2)
    const middleWidth = useAspectRatio
      ? getTextSize(text.substr(0, middle), fontSize)
      : context.measureText(text.substr(0, middle)).width
    const oneCharWiderThanMiddleWidth = useAspectRatio
      ? getTextSize(text.substr(0, middle + 1), fontSize)
      : context.measureText(text.substr(0, middle + 1)).width

    if (middleWidth <= width && oneCharWiderThanMiddleWidth > width) {
      return middle
    }
    if (middleWidth < width) {
      min = middle + 1
    } else {
      max = middle - 1
    }
  }

  return -1
}

/**
 * Canvas 文本换行计算
 * @param text
 * @param width
 * @param font
 * @returns {*[]}
 */
export const wrapText =  (text: string, width: number, font: string, useLetterAspectRatio = false) => {
  const canvas: HTMLCanvasElement = document.createElement('canvas')
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const context: CanvasRenderingContext2D = canvas.getContext('2d')
  const result: string[] = []
  let breakPoint = 0
  const fontSize = parseInt(font, 10) || 12

  if (font) {
    context.font = font
  }

  // while ((breakPoint = findWrapBreak(text, width, <CanvasRenderingContext2D>context)) !== -1) {
  //   result.push(text.substr(0, breakPoint))
  //   text = text.substr(breakPoint)
  // }

  while ((breakPoint = calculateWrapBreak(text, width, context, fontSize, useLetterAspectRatio)) !== -1) {
    result.push(text.substr(0, breakPoint))
    text = text.substr(breakPoint)
  }

  if (text) {
    result.push(text)
  }

  return result
}

/**
 * 计算文本宽度
 * @param text
 * @param font
 */
export const measureText = (text: string, font?: string) => {
  try {
    // 创建 Canvas 元素
    const canvas: HTMLCanvasElement = document.createElement('canvas')
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d') as CanvasRenderingContext2D

    // 设置字体样式
    if (font) {
      context.font = font
    }

    // 计算文本的宽度
    return context?.measureText(text).width
  } catch (e) {
    return 0
  }
}

/**
 * 获取数字和前缀符号
 * 如果传入的是数字，则直接转换为字符串再进行匹配
 * 使用正则表达式来匹配字符串中的整数部分和符号部分
 * 如果匹配失败或数字部分不是有效数字，则返回原数字和空字符串
 * 如果匹配成功，则返回数字和符号
 * input: -1.0 ouput: ['-', '1.0']
 * input: +1,234 ouput: ['+', '1,234']
 * @param num 要分隔的数字，可以是数字或字符串
 * @returns 一个包含两部分的数组，第一部分是数字，第二部分是符号
 */
export const splitValuePrefix = (num: number | string) => {
  const match = String(num).match(/^([+-]?)(\d+(?:\.\d*)?|\.\d+)$/)
  if (!match || isNaN(Number(match[2]))) {
    return [ num, '' ]
  }

  return [ match[2], match[1] || '' ]
}

/**
 * 获取数字和后缀单位
 * input: -1.0% ouput: ['-1.0', '%']
 * input: +1,234元 ouput: ['+1,234', '元']
 * @param {number | string} num - 要分割的数字或字符串
 * @returns {[number | string, string]} - 包含分割后的数字和百分号、任意中文字符的数组
 */
export const splitValueSuffix = (num: number | string) => {
  const match = String(num).match(/([\d,.\-+]+)([^\d,.]*)/)// .match(/^(\d+(?:\.\d*)?|\.\d+)(%|[\u4e00-\u9fa5]+)?$/)
  if (!match || isNaN(Number(match[1].replace(/,/g, '')))) return [ num, '' ]
  return [ match[1], match[2] || '' ]
}

/**
 * 数值转换，不存在的返回 0
 * @param str number or string num
 * @returns
 */
export const ensureNumber = (str: number | string) => {
  const num = Number(str)
  return str === '' || str === null || str === undefined || isNaN(num) ? 0 : num
}

/**
 * 取整
 * @param number
 */
export const mathCeil = (number: number) => {
  const num = Math.ceil(Math.abs(number))
  return number >= 0 ? num : -num
}

/**
 * 字符串转可执行函数
 * @param fn
 * @returns {*}
 * @private
 */
export function _eval (fn: Fn) {
  try {
    // eslint-disable-next-line prefer-rest-params
    return new Function('return ' + fn)(arguments)
  } catch (e) {
    return fn
  }
}


export function hexColorToRGBA (hex, alpha) {
  const rgb = [] // 定义rgb数组
  if (/^\#[0-9A-F]{3}$/i.test(hex)) { // 判断传入是否为#三位十六进制数
    let sixHex = '#'
    hex.replace(/[0-9A-F]/ig, function (kw) {
      sixHex += kw + kw // 把三位16进制数转化为六位
    })
    hex = sixHex // 保存回hex
  }
  if (/^#[0-9A-F]{6}$/i.test(hex)) { // 判断传入是否为#六位十六进制数
    hex.replace(/[0-9A-F]{2}/ig, function (kw) {
      rgb.push(_eval('0x' + kw)) // 十六进制转化为十进制并存如数组
    })
    return `rgba(${rgb.join(',')},${alpha / 100})` // 输出RGB格式颜色
  } else {
    return 'rgb(0,0,0)'
  }
}

/**
 * 获取dataZoom下可视xAxis的data
 */
export const getDataZoomToVisibleXAxisData = ({
  xAxisData,
  chartInstance
}: {
  xAxisData: string[]
  chartInstance: anyObj
}): string[] => {
  if (!chartInstance) {
    return xAxisData
  }

  const option = chartInstance.getOption()
  const dataZoom = option.dataZoom[0]
  if (!dataZoom) {
    return xAxisData
  }

  const { startValue, endValue } = dataZoom
  const visibleData = xAxisData.slice(startValue, endValue + 1)

  // console.log('startValue:', startValue, endValue)
  // console.log('visibleData:', visibleData)
  return visibleData
}

export type AxisLabelLimitRowData = {
  isValid: boolean
  wrapTextData: Array<string[]>
  renderData: string[]
  maxRow: number
}

export const DataAxisLabelLimitRowData: AxisLabelLimitRowData = {
  isValid: false,
  wrapTextData: [],
  renderData: [],
  maxRow: 0
}

/**
 * 按宽度生成换行文本
 * @param textData
 * @param width
 * @param row
 * @param gap
 * @param fontSize
 */
export const generateWrapTextByWidth = ({
  textData,
  width,
  row,
  gap,
  fontSize
}: {
  textData: string[]
  width: number
  row: number
  gap?: number
  fontSize?: number
}): AxisLabelLimitRowData => {
  if (!textData.length || !width) {
    return {
      isValid: false,
      wrapTextData: textData.map(text => [ text ]),
      renderData: textData,
      maxRow: 1
    }
  }

  const baseFontSize = fontSize || 12

  const wrapTextData: Array<string[]> = textData.map((name: string) => {
    const formatWidth = width - (gap ?? 0)
    if (!name) {
      return [ name ]
    }

    return wrapText(name, formatWidth < baseFontSize ? baseFontSize : formatWidth, `${baseFontSize}`, true)
  })

  const maxRow: number = wrapTextData.map(texts => texts.length).sort((a, b) => b - a)[0] || 0

  const renderData = wrapTextData.map(texts => {
    if (texts.length > row) {
      const sliceTexts = texts.slice(0, row)
      const lastText = sliceTexts[sliceTexts.length - 1]
      sliceTexts[sliceTexts.length - 1] = lastText.slice(0, lastText.length - 3) + '...'
      return sliceTexts.join('\n')
    }
    return texts.join('\n')
  })

  return {
    isValid: true,
    wrapTextData,
    renderData,
    maxRow
  }
}

/**
 * 生成axis轴label限制行数显示数据
 */
export const generateAxisLabelLimitRowData = ({
  xAxisData,
  chartInstance,
  row,
  gap,
  fontSize
}: {
  xAxisData: string[]
  chartInstance: anyObj
  row: number
  gap?: number
  fontSize?: number
}): AxisLabelLimitRowData => {
  if (!xAxisData.length || !chartInstance?._coordSysMgr?._coordinateSystems?.[0]?._rect) {
    return {
      isValid: false,
      wrapTextData: xAxisData.map(text => [ text ]),
      renderData: xAxisData,
      maxRow: 1
    }
  }

  let textWidth = 0
  let textGap = 0
  const barChart = chartInstance._chartsViews?.find((item: any) => item.type === 'bar')
  // 优先使用 EC 私有属性，如果没有则手动计算
  if (barChart) {
    const barSize = barChart._data?._layout?.size
    const bandWidth = barChart._data?._layout?.bandWidth
    const barGapValue = Math.max(barSize ? barSize * 0.2 : 0, 0) // 默认 series-bar.barGap = 20%

    textWidth = bandWidth
    textGap = barGapValue * 2
  } else {
    const { width } = chartInstance._coordSysMgr._coordinateSystems[0]._rect
    const singleWidth = width / xAxisData.length

    textWidth = singleWidth
    textGap = 12 * 4
  }

  return generateWrapTextByWidth({
    textData: xAxisData,
    width: textWidth,
    row,
    gap: gap ?? textGap,
    fontSize: fontSize || 12,
  })
}

/**
 * 计算一组数据中正数和负数的最大最小值
 * @param values
 */
export const calculateSums = (values: number[][]): {
  positiveMin: number;
  positiveMax: number;
  negativeMin: number;
  negativeMax: number
} => {
  const positiveSums: number[] = []
  const negativeSums: number[] = []

  for (const subArray of values) {
    let positiveSum = 0
    let negativeSum = 0

    for (const num of subArray) {
      if (num > 0) {
        positiveSum += num
      } else if (num < 0) {
        negativeSum += num
      }
    }

    positiveSums.push(positiveSum)
    negativeSums.push(negativeSum)
  }

  const positiveMin = Math.min(...positiveSums)
  const positiveMax = Math.max(...positiveSums)
  const negativeMin = Math.min(...negativeSums)
  const negativeMax = Math.max(...negativeSums)

  return { positiveMin, positiveMax, negativeMin, negativeMax }
}

export const xAxisLabelEllipsisTooltip = () => {
  const axisMousemove = ({
    params,
    xAxisTooltipId,
    xAxisLabelRow,
    axisLabelLimitRowData,
    tooltipClassName
  }: {
    params: anyObj,
    xAxisTooltipId: string
    xAxisLabelRow: number,
    axisLabelLimitRowData: AxisLabelLimitRowData,
    tooltipClassName?: string
  }) => {
    const { value, event } = params

    let dom = document.querySelector(`#${xAxisTooltipId}`) as HTMLElement
    const hasOldDom = Boolean(dom)

    if (!dom) {
      dom = document.createElement('div')
    }

    const { clientX, clientY } = event.event

    dom.id = xAxisTooltipId
    dom.className = tooltipClassName ? tooltipClassName : 'sino-echarts-x-axis-label-tooltip'
    dom.innerText = value

    const { width = 0, height = 0 } = dom?.getBoundingClientRect() || {}
    const formatHeight = height ? height : 30
    const formatWidth = width

    const tooltipXOffset = clientX - formatWidth / 2
    const tooltipYOffset = clientY - formatHeight - 10

    const styles: anyObj = {
      'transform': `translate(${tooltipXOffset}px, ${tooltipYOffset}px)`,
      'visibility': 'visible',
      'z-index': 10000
    }

    const stylesToString = (styles: anyObj) => {
      return Object.keys(styles).map(key => (`${key}: ${styles[key]}`)).join(';')
    }

    // 防止抖动
    if (!width) {
      styles.visibility = 'hidden'
    } else {
      // 碰撞监测（只做x方向碰撞）
      const { width: viewportWidth } = getViewport()

      // 左侧碰撞
      if (width / 2 > clientX) {
        const overflowX = width / 2 - clientX // 超出量
        styles.transform = `translate(${tooltipXOffset + overflowX}px, ${tooltipYOffset}px)`
      }

      // 右侧碰撞
      if (width / 2 > (viewportWidth - clientX)) {
        const overflowX = width / 2 - (viewportWidth - clientX) // 超出量
        styles.transform = `translate(${tooltipXOffset - overflowX}px, ${tooltipYOffset}px)`
      }
    }

    const getCurrentValueIsEllipsis = () => {
      if (xAxisLabelRow > 1 && !axisLabelLimitRowData.isValid) {
        return false
      }

      const currentValueWrapTextData = axisLabelLimitRowData.wrapTextData.find(texts => texts.join('') === value)
      if (currentValueWrapTextData && currentValueWrapTextData.length > xAxisLabelRow) {
        return true
      } else {
        return false
      }
    }

    if (!getCurrentValueIsEllipsis() && styles.visibility !== 'hidden') {
      styles.visibility = 'hidden'
    }

    dom.style.cssText = stylesToString(styles)

    !hasOldDom && document.body.appendChild(dom)
  }

  const axisMouseout = (xAxisTooltipId: string) => {
    const dom = document.querySelector(`#${xAxisTooltipId}`) as HTMLElement
    if (dom) {
      dom.style.cssText = 'visibility: hidden; opacity: 0;'
    }
  }

  const removeTooltip = (xAxisTooltipId: string) => {
    const dom = document.querySelector(`#${xAxisTooltipId}`)
    if (dom) {
      document.body.removeChild(dom)
    }
  }

  return {
    axisMousemove,
    axisMouseout,
    removeTooltip
  }
}
